import { Callout } from "@blueprintjs/core";
import Select, { MultiValue } from "react-select";

import { Box } from "@/components/layout/flexbox";
import { ReleaseComponentOption } from "@/components/pages_logged_in/program/components/ReleaseComponentOption";
import { StyledFormGroup } from "@/components/pages_logged_in/program/components/set-components/StyledFormGroup";
import { ALL_PARTICIPANTS_OPTION } from "@/components/pages_logged_in/program/constants";
import { SimpleProgramUsersQuery } from "@/graphql";
import { MainComponent } from "@/types/programs";
import { SelectOption } from "@/types/select";

/**
 * Interface defining the props for ParticipantFormInput component
 */
interface ParticipantFormInputProps {
  participants: SelectOption[]; // Selected participants
  setParticipants: (value: SelectOption[]) => void;
  isSelectedAllParticipants: boolean;
  participantOptions: SelectOption[]; // Options for the participant selector
  programComponent: MainComponent;
  isReleaseDependentComponents?: boolean;
  setIsReleaseDependentComponents?: (value: boolean) => void;
  loadingProgramUser: boolean;
  programUserData?: SimpleProgramUsersQuery;
}

/**
 * Component for configuring participant settings for a program element
 */
export function ParticipantFormInput({
  participants,
  setParticipants,
  isSelectedAllParticipants,
  participantOptions,
  programComponent,
  isReleaseDependentComponents = false,
  setIsReleaseDependentComponents = () => {},
  loadingProgramUser,
  programUserData,
}: ParticipantFormInputProps) {
  // Check if there are no participant candidates
  const _isEmptyParticipantCandidates =
    !programUserData?.simpleProgramUsers?.length &&
    !programUserData?.pendingParticipantEmails?.length;

  // Determine if we should display a warning about the "all participants" option
  const _isDisplayAllOptionWarning =
    !loadingProgramUser &&
    _isEmptyParticipantCandidates &&
    !isSelectedAllParticipants;

  /**
   * Handle change in release dependent components setting
   */
  const handleReleaseDependentComponentsChange = (value: boolean) => {
    setIsReleaseDependentComponents(value);
  };

  /**
   * Updates participant selection, ensuring "All Participants" is exclusive
   */
  const handleParticipantsChange = (
    selectedParticipants: MultiValue<SelectOption>,
  ) => {
    const participants = Array.from(selectedParticipants);
    const forAllParticipants = participants?.find(
      (participant) => participant.value === ALL_PARTICIPANTS_OPTION.value,
    );
    setParticipants(forAllParticipants ? [forAllParticipants] : participants);
  };

  return (
    <StyledFormGroup label="Participants">
      <Select
        value={participants}
        onChange={handleParticipantsChange}
        isSearchable={true}
        options={
          isSelectedAllParticipants
            ? [ALL_PARTICIPANTS_OPTION]
            : participantOptions
        }
        hideSelectedOptions={true}
        closeMenuOnSelect={false}
        isMulti
        styles={{
          menu: (baseStyles) => ({
            ...baseStyles,
            zIndex: 999,
          }),
          control: (baseStyles) => ({
            ...baseStyles,
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            padding: "1px 8px",
          }),
        }}
      />

      {_isDisplayAllOptionWarning && (
        <Box mt={2}>
          <Callout intent="warning">
            The programs currently don't have any participants.{" "}
            <b>The All Program Participants </b> option will allow future
            participants to take part in this component.
          </Callout>
        </Box>
      )}

      {/* Release dependent components option - shown when changing from all participants to specific participants */}
      {!isSelectedAllParticipants &&
        !!participants?.length &&
        programComponent?.forAllParticipants &&
        !!programComponent?.dependentComponents?.length && (
          <Box mt={2}>
            <Callout intent="warning">
              <ReleaseComponentOption
                component={programComponent}
                warningTitle="Updating this component will affect others that depend on it"
                defaultValue={isReleaseDependentComponents}
                onChange={handleReleaseDependentComponentsChange}
              />
            </Callout>
          </Box>
        )}
    </StyledFormGroup>
  );
}
